import { getCookie } from '../utils/cookie';
import { baseUrl } from '../constants/app';

class SseNotificationService {
  constructor() {
    this.eventSource = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.listeners = new Map(); // event type -> array of callbacks
    this.connectionStatus = 'disconnected'; // disconnected, connecting, connected, error
    this.reconnectTimer = null;
  }

  connect() {
    if (this.eventSource && this.eventSource.readyState !== EventSource.CLOSED) {
      return;
    }

    const token = getCookie('auth');
    if (!token) {
      console.error('[SSE] No authentication token found');
      this.updateStatus('error');
      return;
    }

    this.updateStatus('connecting');

    const url = `${baseUrl}/orders/notifications/stream?token=${token}`;

    try {
      this.eventSource = new EventSource(url);

      this.eventSource.onopen = () => {
        this.reconnectAttempts = 0;
        this.updateStatus('connected');
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          this.handleMessage(data);
        } catch (err) {
          console.error('[SSE] Failed to parse message:', err);
        }
      };

      this.eventSource.onerror = (error) => {
        console.error('[SSE] Connection error:', error);
        this.updateStatus('error');
        this.eventSource.close();
        this.attemptReconnect();
      };
    } catch (err) {
      console.error('[SSE] Failed to create EventSource:', err);
      this.updateStatus('error');
      this.attemptReconnect();
    }
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.reconnectAttempts = 0;
    this.listeners.clear();
    this.updateStatus('disconnected');
  }

  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SSE] Max reconnection attempts reached');
      this.updateStatus('error');
      return;
    }

    this.reconnectAttempts++;

    // Exponential backoff: 2s, 4s, 8s, 16s, 32s
    const delay = Math.min(2000 * Math.pow(2, this.reconnectAttempts - 1), 32000);

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  handleMessage(data) {
    const { type } = data;
    const callbacks = this.listeners.get(type) || [];

    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (err) {
        console.error(`[SSE] Error executing callback for ${type}:`, err);
      }
    });

    const wildcardCallbacks = this.listeners.get('*') || [];
    wildcardCallbacks.forEach(callback => {
      try {
        callback(data);
      } catch (err) {
        console.error('[SSE] Error executing wildcard callback:', err);
      }
    });
  }

  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }

    this.listeners.get(eventType).push(callback);
  }

  off(eventType, callback) {
    const callbacks = this.listeners.get(eventType) || [];
    const filtered = callbacks.filter(cb => cb !== callback);
    this.listeners.set(eventType, filtered);
  }

  updateStatus(status) {
    const oldStatus = this.connectionStatus;
    this.connectionStatus = status;

    if (oldStatus !== status) {
      const callbacks = this.listeners.get('status_change') || [];
      callbacks.forEach(callback => {
        try {
          callback(status);
        } catch (err) {
          console.error('[SSE] Error executing status_change callback:', err);
        }
      });
    }
  }

  getStatus() {
    return this.connectionStatus;
  }

  isConnected() {
    return this.connectionStatus === 'connected';
  }
}

const sseNotificationService = new SseNotificationService();
export default sseNotificationService;
